import { useRef, useEffect, useCallback } from 'react';
import { To, useNavigate } from 'react-router-dom';

/**
 * A hook that provides safe navigation by ensuring the component
 * is still mounted before attempting to navigate.
 *
 * This prevents "Failed to execute 'insertBefore' on 'Node'" errors
 * that can occur when navigation is triggered after a component is unmounted.
 *
 * @returns A safe navigation function wrapped around React Router's navigate
 */
export const useSafeNavigation = () => {
  const navigate = useNavigate();
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const safeNavigate = useCallback(
    (to: To | -1, options?: { replace?: boolean; state?: any }) => {
      if (isMounted.current) {
        return navigate(to as To, options);
      }
      return undefined;
    },
    [navigate],
  );

  return { safeNavigate };
};
